/* eslint-disable @typescript-eslint/ban-ts-comment */
import { PaginatedDealResult } from 'types/deal';
import { PortfolioSignal } from 'types/portfolioSignal';
import { User } from 'types/user';
import { UserRoles } from 'types/userRoles';

import { deleteMethod, get, post, put } from './helpers';
import { ProblemClientResult } from '../types/problemClient';
import { Tariff } from '../types/tariffInfo';
import { Chart } from './types/chart';
import { Deal } from './types/deal';
import { Email } from './types/email';
import { AccountStatResult } from '../types/accountStat';
import { AggregateInvestbox } from '../types/aggregateInvestbox';
import { AggregateStrategy } from '../types/aggregateStrategy';
import { Currency } from '../types/aggregateStrategyInfoRef';
import { AggregateUser } from '../types/aggregateUser';
import { Analytic } from '../types/analytic';
import { AuditActionInfo, AuditLog } from '../types/auditLog';
import { Option, PackageOption } from '../types/option';
import { Search, SearchPosition } from './types/search';
import { SignalClose } from './types/signal';
import { UserChangePassword, UserLogin, LoginSmsCode } from './types/user';
import { Banner } from '../types/banner';
import { Chart as StrategyChart } from '../types/chart';
import { ClientPnlHistory, StatusEnum } from '../types/clientDetails';
import { ClientAccountInfo } from '../types/clientFullInfo';
import { ClientSignal } from '../types/clientSignal';
import { DelayedSignal, PaginatedDelayedSignal } from '../types/delayedSignal';
import { Category, Question } from '../types/faq';
import { MarketSchedule, Weekend, SecurityInfo as ScheduleSecurity } from '../types/marketSchedule';
import { PageParams } from '../types/pagination';
import { Position } from '../types/position';
import { Promotion, PromotionRequest } from '../types/promotion';
import { RequestModerate } from '../types/requestModerate';
import { SecurityBidAsk, SecurityCS, SecurityInfo, SecurityMarketData } from '../types/securityinfo';
import { SignalSettings } from '../types/signalSettings';
import { ResultPage, StandartResponse } from '../types/standartResponse';
import { Strategy } from '../types/strategy';
import { ApiStrategyPortfolio } from '../types/strategyPortfolio';
import { UnsubscribeClientResult } from '../types/unsubscribeClient';
import { Nullable } from '../utils/types';
import { getStringQuery } from '../utils/url';
import { FileDownload, BriefAgreementInfo, MailingResult } from './types/contracts';
import { ApiDataResponse } from './types/response';

export const getStrategies = () => get<ApiDataResponse<Strategy[]>>('/strategies');

export const getPositions = (strategyId: string) =>
  get<ApiDataResponse<Position[]>>(`/strategies/openpositions/${strategyId}`);

export const getSandboxPositions = (strategyId: string) =>
  get<ApiDataResponse<Position[]>>(`/sandbox/openpositions/${strategyId}`);

export const getDeals = (params: Deal) =>
  post<ApiDataResponse<PaginatedDealResult>>(`/strategies/alltrades/${params.strategyId}`, params.data);

export const getSandboxDeals = (params: Deal) =>
  post<ApiDataResponse<PaginatedDealResult>>(`/sandbox/alltrades/${params.strategyId}`, params.data);

export const getChart = (params: Chart) =>
  get<ApiDataResponse<StrategyChart>>(`/strategies/history/${params.strategyId}/${params.period}`);

export const getSandboxChart = (params: Chart) =>
  get<ApiDataResponse<StrategyChart>>(`/sandbox/history/${params.strategyId}/${params.period}`);

export const getUser = () => get('/account/current');

export const getUserById = (id: number) => get<ApiDataResponse<User>>(`/users/${id}`);

export const login = (userData: UserLogin) => post('/account/signinstage', userData);

export const checkLoginSms = (data: LoginSmsCode) => post('/account/signin', data);

export const logout = () => get('/account/signout');

export const changePassword = (userData: UserChangePassword) => post(`/account/changepwd`, userData);

export const getPortfolio = (strategyId?: string) =>
  get<ApiDataResponse<ApiStrategyPortfolio>>(`/strategies/portfolio/${strategyId}`);

export const getSandboxPortfolio = (strategyId?: string) =>
  get<ApiDataResponse<ApiStrategyPortfolio>>(`/sandbox/portfolio/${strategyId}`);

export const closePosition = (params: SignalClose) => post<ApiDataResponse<unknown>>(`/signal/close`, params);

export const search = (params: Search) => post(`/securities/search`, params);

export const getPositionInfo = (params: SearchPosition) => post(`/securities/info`, params);

export const newMarketSignal = (params: Partial<Position>) => post<ApiDataResponse<unknown>>(`/signal/new`, params);

export const newMarketLimitSignal = (params: Partial<Position>) =>
  post<ApiDataResponse<unknown>>(`/signal/limit`, params);

export const cancelMarketLimitSignal = (params: { strategyId: string; managerId: number }) =>
  post<ApiDataResponse<unknown>>(`/signal/limitcancel`, params);

export const newMultipleMarketSignal = (params: Partial<Position>[]) =>
  post<ApiDataResponse<null>>(`/signal/multiple`, params);

export const newDelayedSignal = (params: Partial<DelayedSignal>) =>
  post<ApiDataResponse<null>>(`/signal/delayed`, params);

export const changeSignal = (params: Partial<Position>) =>
  post<ApiDataResponse<Position>>('/signal/replaceposition', params);

export const getDelayedSignals = (params: Deal) =>
  post<ApiDataResponse<PaginatedDelayedSignal>>(`/strategies/delayedsignals/${params.strategyId}`, params.data);

export const getSandboxDelayedSignals = (params: Deal) =>
  post<ApiDataResponse<PaginatedDelayedSignal>>(`/sandbox/delayedsignals/${params.strategyId}`, params.data);

export const closeDelayedSignal = (signalId: Nullable<string>) =>
  deleteMethod<ApiDataResponse<unknown>>(`/signal/delayed/${signalId}`);

export const getAppConfig = () => get(`/info`);

export const getServicesConfig = () => get('/info/services');

export const getSignalSettings = () => get<ApiDataResponse<SignalSettings>>('/info/signal-settings');

export const getMoneyStatistic = (params: PageParams) => post('/stat/strategies', params);

export const getClientsStatistic = (params: PageParams) =>
  post<ApiDataResponse<AccountStatResult>>('/stat/clients', params);

export const getUnsubscribeClientsStatistic = (params: PageParams) =>
  post<UnsubscribeClientResult>('/stat/clients/unsubscribed', params);

export const getSecurityInfo = (searchCriteria: string) =>
  post<ApiDataResponse<SecurityInfo>>('/admin/securities/info', searchCriteria);

export const getProblemClientsStatistic = (params: PageParams) =>
  post<ApiDataResponse<ProblemClientResult>>('/clients/problem', params);

export const getSecurityBidAsk = (searchCriteria: string) =>
  post<ApiDataResponse<SecurityBidAsk>>('/admin/securities/fromrmds', searchCriteria);

export const getSecurityCS = (searchCriteria: string) =>
  get<ApiDataResponse<SecurityCS>>(`/admin/securities/fromcs/${searchCriteria}`);

export const getSecurityMarketData = (searchCriteria: string) =>
  post<ApiDataResponse<SecurityMarketData>>('/admin/securities/endofday', searchCriteria);

export const downloadDeals = (strategyId?: string) =>
  get<ApiDataResponse<FileDownload>>(`/strategies/download/trades/${strategyId}`);

export const downloadSandboxDeals = (strategyId?: string) =>
  get<ApiDataResponse<FileDownload>>(`/sandbox/download/trades/${strategyId}`);

export const downloadHistory = (strategyId?: string) =>
  get<ApiDataResponse<FileDownload>>(`/strategies/download/history/${strategyId}`);

export const downloadSandboxHistory = (strategyId?: string) =>
  get<ApiDataResponse<FileDownload>>(`/sandbox/download/history/${strategyId}`);

export const getStrategiesParams = () => get('/admin/strategies/summary');

export const getAnalytics = (params: PageParams) =>
  post<ApiDataResponse<ResultPage<Analytic>>>('/admin/analytics', params);

export const updateAnalytic = (data: Partial<Analytic>) =>
  post<ApiDataResponse<Analytic>>('/admin/analytics/edit', data);

export const getAuthorDictionary = () => get<ApiDataResponse<Option[]>>('/users');

export const getCategoryDictionary = () => get<ApiDataResponse<Option[]>>('/admin/analytics/categories');

export const getUsersAdministration = (params: PageParams) =>
  post<ApiDataResponse<ResultPage<AggregateUser>>>('/users', params);

export const getFullListStrategiesAdministration = () =>
  get<ApiDataResponse<AggregateStrategy[]>>('/v2/admin/strategies');

export const getStrategiesAdministration = (params: PageParams) => post('/admin/strategies', params);

export const getInvestboxesAdministration = (params: PageParams) =>
  post<ApiDataResponse<ResultPage<AggregateInvestbox>>>('/admin/investboxes', params);

export const getAdministrationAuditLogs = (params: PageParams) =>
  post<ApiDataResponse<ResultPage<AuditLog>>>('/admin/AuditLog/logs', params);

export const getAdministrationAuditActions = () => get<ApiDataResponse<AuditActionInfo[]>>('/admin/AuditLog/actions');

export const getUserRoles = () => get<ApiDataResponse<UserRoles[]>>('/admin/users/roles');

export const updateUser = (data: Partial<AggregateUser>) =>
  post<ApiDataResponse<AggregateUser>>('/admin/users/edit', data);

export const removeUser = (id: number) => deleteMethod<ApiDataResponse<unknown>>(`/admin/users/${id}`);

export const updateStrategy = (data: Partial<AggregateStrategy>) =>
  post<ApiDataResponse<AggregateStrategy>>('/admin/strategies/edit', data);

export const updateInvestbox = (data: Partial<AggregateInvestbox>) =>
  post<StandartResponse<AggregateInvestbox>>('/admin/investboxes/edit', data);

export const changeStrategy = (data: { id: string; open?: boolean; active?: boolean }) =>
  post<ApiDataResponse<{ id: string; active: boolean }>>('/admin/strategies/state', data);

export const addStrategy = (data: Partial<AggregateStrategy>) => post('/admin/strategies/add', data);

export const publishStrategyToEva = (id: string) =>
  post<ApiDataResponse<unknown>>('/admin/strategies/publishToEva', id);

export const publishStrategyToBorr = (id: string) =>
  post<ApiDataResponse<unknown>>('/admin/strategies/publishToBorr', id);

export const updateStrategyInEva = (id: string) => post<ApiDataResponse<unknown>>('/admin/strategies/updateInEva', id);

export const freezeStrategy = (data: { id: string; freeze: boolean }) =>
  post<ApiDataResponse<unknown>>('/admin/strategies/freeze', data);

export const rebalanceStrategy = (id: string) => post<ApiDataResponse<unknown>>(`/admin/strategies/rebalance/${id}`);

export const sendStrategyToCs = (id: string) => post<ApiDataResponse<unknown>>(`/admin/strategies/pushtocs/${id}`);

export const addInvestbox = (data: Partial<AggregateInvestbox>) =>
  post<ApiDataResponse<AggregateInvestbox>>('/admin/investboxes/add', data);

// *** admin tools
export const getClientPersInfo = (agreementId: string) =>
  get<ApiDataResponse<ClientAccountInfo | null>>(`/admin/clients/info/${agreementId}`);

export const rebalanceClient = (data: { agreementId: string }) => post('/admin/clients/rebalance', data);

export const calcRebalanceClient = (agreementId: string) => get(`/admin/clients/calc_rebalance/${agreementId}`);

export const attachClient = (data: { agreementId: string; strategy: string; status: StatusEnum }) =>
  post<ApiDataResponse<unknown> & { message?: string }>('/admin/clients/attach', data);

export const detachClient = (agreementId: string) =>
  post<ApiDataResponse<unknown>>(`/admin/clients/detach/${agreementId}`);

export const blockClient = (data: { agreementId: string; time: string }) => post('/admin/clients/block', data);

export const stopClientService = () => post('/admin/management/stopcs');

export const recalcStrategyHistory = (data: { id: string }) => post(`/admin/management/recalc_history/${data?.id}`);

export const getCsDiag = () => get('/admin/management/csdiag');

export const downloadStrategyChart = (data: { id: string }) =>
  get<ApiDataResponse<FileDownload>>(`/admin/strategies/rawhistory/${data?.id}`);

export const getFollowPrecision = (data: { id: string }) => post(`/admin/strategies/follow/${data?.id}`);

export const getClientSignal = (params: { agreementId: string; startDate?: string }) =>
  get<ApiDataResponse<ClientSignal[]>>(
    `/clients/signals/${params.agreementId}${getStringQuery({ startDate: params.startDate })}`,
  );

export const getLimitsRmds = (agreementId: string) => get(`/admin/clients/rmdslimits/${agreementId}`);

export const getLimitsQrc = (agreementId: string) => get(`/admin/clients/qrclimits/${agreementId}`);

export const getCurrencyInfo = () => get<ApiDataResponse<Currency[]>>('/admin/strategies/currencies');

export const getStrategyInfo = (id: string) => get<ApiDataResponse<AggregateStrategy>>(`/admin/strategies/${id}`);

export const getInvestboxInfo = (id: string) => get<ApiDataResponse<AggregateInvestbox>>(`/admin/investboxes/${id}`);

export const getUserInfo = (size: number) => get(`/admin/strategies/users/${size}`);

export const getStrategyInfoRefs = (size: number) => get(`/admin/strategies/refs/${size}`);

export const getClientInfo = (tickers: string[]) =>
  post<ApiDataResponse<BriefAgreementInfo[]>>('/admin/strategies/agreements', tickers);

export const getBanners = (params: PageParams) => post<ApiDataResponse<ResultPage<Banner>>>('/admin/banners', params);

export const updateBanners = (data: Partial<Banner>) => post<ApiDataResponse<Banner>>('/admin/banners/edit', data);

export const split = (params: Partial<Position>) => post<ApiDataResponse<null>>('/admin/strategies/split', params);

export const toChild = (data: { strategyId: string; parentStrategyId: string; useSelfSignals: Nullable<boolean> }) =>
  post<ApiDataResponse<unknown>>(
    `/admin/strategies/${data.strategyId}/makechildof/${data.parentStrategyId}/${data.useSelfSignals ?? false}`,
  );

export const getUtmDictionary = () => get<ApiDataResponse<Option[]>>('/admin/banners/utms');

export const getStatisticStrategiesDictionary = () => get<ApiDataResponse<Option[]>>('/stat/registry/strategies');

export const getStatisticAuthorsDictionary = () => get('/stat/registry/authors');

export const getStatisticServicesDictionary = () => get<ApiDataResponse<Option[]>>('/stat/registry/services');

export const getStatisticProblemsDictionary = () => get<ApiDataResponse<Option[]>>('/clients/registry/problems');

export const getClientInfoDetails = (ticker: string) => post(`/clients/find`, ticker);

export const getClientInfoDetailsById = (agreementId: string) => get(`/clients/info/${agreementId}`);

export const getClientHistoryDetails = (params: { agreementId: string; sortType?: string; startDate?: string }) =>
  get(
    `/clients/history/${params.agreementId}/${params.sortType || ' '}${getStringQuery({
      startDate: params.startDate,
    })}`,
  );

export const getClientPositionsDetails = (agreementId: string) => get(`/clients/positions/${agreementId}`);

export const getClientChartDetails = (params: { agreementId: string; startDate?: string }) =>
  get(`/clients/follow/${params.agreementId}${getStringQuery({ startDate: params.startDate })}`);

export const getClientPnlHistory = (params: { agreementId: string; period: string }) =>
  get<ApiDataResponse<ClientPnlHistory>>(`/clients/pnlhistory/${params.agreementId}/${params.period}`);

export const getClientAccepts = (agreementId: Nullable<string>) => get(`/clients/accepts/${agreementId}`);

export const downloadClientIirs = (params: { agreementId: string; dateFrom: string; dateTo: string }) =>
  get<Response | Partial<ApiDataResponse<unknown>>>(
    `/clients/iirs/${params.agreementId}${getStringQuery({ dateFrom: params.dateFrom, dateTo: params.dateTo })}`,
  );

// @ts-ignore
export const getErrorsStatistic = (data) => post('/stat/errors', data);

// @ts-ignore
export const getErrorsByStrategyStatistic = (data) => post('/stat/errorsByStrategy', data);

// @ts-ignore
export const sandboxUploadFileData = (data) => post('/sandbox/uploadtrack', data);

export const sandboxDeleteTrack = (strategyId: string) => deleteMethod(`/sandbox/${strategyId}`);

export const sandboxTrackStatus = (strategyId: string) => get(`/sandbox/status/${strategyId}`);

export const downloadSandboxTrack = (strategyId: string) => get(`/sandbox/track/${strategyId}`);

export const sandboxRequestModerate = (params: RequestModerate) =>
  post(`/strategies/requestmoderate/${params.strategyId}/${params.action}`);

export const sandboxModerate = (params: RequestModerate) =>
  post(`/strategies/moderate/${params.strategyId}/${params.action}`);

export const toSandbox = (data: { strategyId: string }) => post(`/strategies/tosandbox/${data.strategyId}`);

export const updateHistory = () => get<ApiDataResponse<null>>('/strategies/updatehistory');

export const newPortfolioSignal = (data: PortfolioSignal) => post<ApiDataResponse<null>>(`/signal/construct`, data);

export const canSendSignal = (strategyId: string) =>
  get<ApiDataResponse<{ secondsLeft: number | null; isLimit: boolean; executingNumber: number | null }>>(
    `/signal/canregistersignal/${strategyId}`,
  );

// schedule api
export const getSchedule = () => get<ApiDataResponse<MarketSchedule[]>>('/admin/schedule');

export const getForbiddenSecurities = () => get<ApiDataResponse<ScheduleSecurity[]>>('/admin/schedule/forbidden');

export const setScheduleTimes = (data: MarketSchedule) =>
  post<ApiDataResponse<MarketSchedule>>('/admin/schedule/add', data);

export const addSchedule = (data: MarketSchedule) => post<ApiDataResponse<MarketSchedule>>('/admin/schedule/add', data);

export const addScheduleWeekend = (data: Weekend) => post<ApiDataResponse<unknown>>('/admin/schedule/weekend', data);

export const removeScheduleWeekend = (data: Weekend) =>
  deleteMethod<ApiDataResponse<unknown>>('/admin/schedule/weekend', data);

export const getClassCodes = () => get<ApiDataResponse<string[]>>('/admin/schedule/codes');

export const removeSchedule = (id: string) => post<ApiDataResponse<MarketSchedule>>(`/admin/schedule/remove/${id}`);

export const getAuditIir = (params: Record<string, string | null>) => get(`/audit/iirs/${getStringQuery(params)}`);

export const getAuditOrder = (params: Record<string, string | null>) => get(`/audit/orders/${getStringQuery(params)}`);

export const getFaqCategories = () => get<Category[]>('/admin/faq/categories');
export const addFaqCategory = (category: Partial<Category>) =>
  post<ApiDataResponse<unknown>>('/admin/faq/categories', category);
export const editFaqCategory = (category: Category) =>
  put(`/admin/faq/categories/${category.id}`, { name: category.name, friendlyUrl: category.friendlyUrl });
export const deleteFaqCategory = (id: string) => deleteMethod(`/admin/faq/categories/${id}`);

export const getFaqQuestions = () => get<Question[]>('/admin/faq/questions');
export const addFaqQuestion = (question: Partial<Question>) =>
  post<ApiDataResponse<unknown>>('/admin/faq/questions', question);
export const editFaqQuestion = (question: Question) => put(`/admin/faq/questions/${question.id}`, question);
export const deleteFaqQuestions = (id: string) => deleteMethod(`/admin/faq/questions/${id}`);

export const getFaqCategoryQuestions = (categoryId: string) => get(`/admin/faq/categories/${categoryId}`);
export const editFaqCategoryQuestions = (params: { categoryId: string; questionIds: string[] }) =>
  post(`/admin/faq/categories/${params.categoryId}/questions`, params.questionIds);

export const getComment = (strategyId: string, date: string) =>
  get<ApiDataResponse<{ comment: string | null }>>(`/strategies/strategysignalcomment/${strategyId}/${date}`);

export const updateComment = (strategyId: string, date: string, comment: string) =>
  post(`/strategies/strategysignalcomment`, { strategyId, date, comment });

export const sendEmail = (data: Email) => post<ApiDataResponse<MailingResult>>('/admin/mails/send', data);

export const getApiKey = (strategyId: string) =>
  post<ApiDataResponse<string | null>>(`/admin/strategies/${strategyId}/generate-api-key`);

export const getBranches = () => get<ApiDataResponse<Option[]>>('/branch');

export const getServicePackages = () => get<PackageOption[]>('/admin/tariffs/service-packages');

export const getTariffs = () => get<Tariff[]>('/admin/tariffs');

export const getReport = (agreementId: string | undefined) =>
  get<ApiDataResponse<{ contentBase64: string; fileName: string }>>(`/clients/report/${agreementId}`);

export const getUnbindReport = (agreementId: string | undefined) =>
  get<ApiDataResponse<{ contentBase64: string; fileName: string }>>(`/clients/unbindreport/${agreementId}`);

export const getPromotions = (params: PageParams) => post<ResultPage<Promotion>>('/campaigns', params);

export const getPromotion = (id: number) => get<Promotion>(`/campaigns/${id}`);

export const createPromotion = (data: Omit<PromotionRequest, 'id'>) => post<Promotion>('/campaigns/new', data);

export const updatePromotion = (id: number, data: PromotionRequest) => put<Promotion>(`/campaigns/${id}`, data);

export const activatePromotion = (id: number) => put<null>(`/campaigns/activate/${id}`, {});

export const deactivatePromotion = (id: number) => put<null>(`/campaigns/deactivate/${id}`, {});
